import { graphql } from 'app/generated/gql'
import { EditPencil as EditPencilIcon, Reply as ReplyIcon } from 'iconoir-react'
import { capitalize } from 'lodash'
import { ReactElement, useCallback, useState } from 'react'
import { useQuery } from 'urql'
import { RichTextEditor } from '../editor/RichTextEditor'
import NameIcon from '../shared/NameIcon'
import Spinner from '../Spinner'
import { Tooltip } from '../Tooltip'
import { ActivityCard } from './ActivityCard'
import { ActivityTags } from './ActivityTags'
import { FollowUpForm } from './FollowUpForm'
import { KeyValueDrawer } from './KeyValueDrawer'
import { formatDate } from 'app/date.utils'
import type { FollowUpQuery } from 'app/generated/gql/graphql'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { FollowUpJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { assertBoom } from 'lib/assertBoom'

const FOLLOWUP_QUERY = graphql(`
  query FollowUp($id: ID!) {
    followUp(id: $id) {
      id
      orgId
      workspaceId
      locationId
      contactId
      ownerName
      ownerId
      appointmentDate
      followUpDate
      isConnected
      isScheduled
      description
      createdDate
      updatedDate
    }
  }
`)

function mapFollowUp(followUp: FollowUpQuery['followUp']): FollowUpJson {
  return {
    id: followUp.id,
    orgId: followUp.orgId,
    workspaceId: followUp.workspaceId,
    locationId: followUp.locationId,
    appointmentDate: followUp.appointmentDate ?? undefined,
    followUpDate: followUp.followUpDate ?? undefined,
    isConnected: followUp.isConnected ?? undefined,
    isScheduled: followUp.isScheduled ?? undefined,
    description: followUp.description ?? undefined,
    owner: followUp.ownerId ? { id: followUp.ownerId, name: followUp.ownerName ?? undefined } : undefined,
  }
}

type Props = {
  activity: ActivityJson
  alwaysOpen?: boolean
  contact?: ContactJson
  isFirst?: boolean
  isLast?: boolean
  singleActivity?: boolean
  onChange?: (id: string) => void
  onActivityUpdated?: (activity: ActivityJson) => void
  handleEdit?: (activity: ActivityJson) => void
  handleDelete?: () => void
}

export function FollowUpCard({
  activity,
  alwaysOpen,
  contact,
  isFirst,
  isLast,
  singleActivity,
  onChange,
  onActivityUpdated,
  handleEdit,
  handleDelete,
}: Props): ReactElement {
  assertBoom(activity.type === `FollowUp`)
  const [nameTooltip, setNameTooltip] = useState<string | undefined>(undefined)
  const [modalProps, setModalProps] = useState<{
    title: string
    isFullScreen: boolean
    overflowHidden: boolean
    closable?: boolean
    removePadding?: boolean
    useRadix?: boolean
  }>({
    title: `Edit follow up`,
    isFullScreen: false,
    overflowHidden: false,
    closable: true,
    removePadding: true,
    useRadix: true,
  })
  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: FOLLOWUP_QUERY,
    variables: { id: activity.typeId! },
    pause: !activity.typeId,
  })
  const followUp = data?.followUp ? mapFollowUp(data.followUp) : undefined
  const isLoading = fetching && !followUp

  const getDrawerContent = (isPreview: boolean) => (
    <div className="w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner size={4} />
        </div>
      ) : followUp ? (
        <>
          <KeyValueDrawer
            map={{
              'Connected?': followUp?.isConnected ? `Yes` : `No`,
              'Scheduled?': followUp?.isScheduled ? `Yes` : `No`,
              'Date of Appointment': followUp?.appointmentDate
                ? formatDate({ value: followUp?.appointmentDate, dateOnly: true })
                : `-`,
            }}
          />
          {!isPreview && (
            <div className="mt-3 flex flex-col gap-y-0.5">
              <span className="text-body-xs text-subtle">Note</span>
              <div className="items-center">
                <RichTextEditor namespace="followUp-view" editable={false} text={followUp?.description || `-`} />
              </div>
            </div>
          )}
        </>
      ) : (
        error && <div className="w-full text-center text-strong">{capitalize(error.message)}</div>
      )}
    </div>
  )

  const getTags = useCallback(
    () =>
      isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner size={4} />
        </div>
      ) : (
        <ActivityTags activityTags={activity.tags} workspaceId={activity.workspaceId} isLoading={isLoading} />
      ),
    [activity, isLoading],
  )

  const getFollowUpForm = () => (
    <FollowUpForm
      contact={contact!}
      followUp={followUp}
      onSubmit={async (responseActivity, responseFollowUp) => {
        if (responseActivity) {
          handleEdit?.(responseActivity)
        }
        onChange?.(activity.id!)
        if (responseFollowUp?.followUpDate) {
          onActivityUpdated?.({ ...activity, activityDate: responseFollowUp.followUpDate })
        }
        await reexecuteQuery({ requestPolicy: `network-only` })
      }}
      changeModalProps={() => setModalProps({ ...modalProps, title: `Update in progress`, closable: false })}
      onDelete={() => {
        handleDelete?.()
        closeModal()
      }}
      onComplete={closeModal}
    />
  )

  return (
    <ActivityCard
      activityDate={activity?.activityDate}
      tags={getTags()}
      icon={<ReplyIcon className="h-5 w-5 rounded-md bg-green-600 p-1 text-on-color" stroke="1.5" />}
      title={
        <div className="flex min-w-0 items-center gap-x-1 truncate text-ui-sm text-strong">
          Follow Up
          {activity.owner && (
            <>
              <span className="text-body-sm text-subtle">{` by `}</span>
              <NameIcon
                widthClassName="w-5"
                heightClassName="h-5"
                backgroundClassName="bg-surface-strong border border-default rounded-full"
                textClassName="text-strong"
                fullName={`${activity.owner?.name?.[0]}`}
                sizeStyle={{ fontSize: `8px` }}
              />
              <Tooltip
                className="min-w-0 truncate"
                tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
                message={nameTooltip}
                onMouseOver={(event) => {
                  const el = event.currentTarget as HTMLElement
                  const isOverflowing = el.scrollWidth > el.clientWidth
                  setNameTooltip(isOverflowing ? activity.owner?.name || undefined : undefined)
                }}
              >
                {activity.owner?.name}
              </Tooltip>
            </>
          )}
        </div>
      }
      actions={
        <div className="flex gap-x-1 p-0.5">
          <Tooltip message="Edit Follow Up">
            <EditPencilIcon
              className="h-3 w-3 cursor-pointer"
              strokeWidth={1}
              aria-label={`Edit ${activity.type}`}
              onClick={() => showModal(getFollowUpForm(), modalProps)}
            />
          </Tooltip>
        </div>
      }
      preview={getDrawerContent(true)}
      drawer={getDrawerContent(false)}
      isFirst={isFirst}
      isLast={isLast}
      alwaysOpen={alwaysOpen}
      singleActivity={singleActivity}
    />
  )
}
